/**
 * Slash Command Autocomplete Component
 * 
 * Provides autocomplete functionality for prompt templates when users type "/" in chat inputs.
 * Features fuzzy search, keyboard navigation, and template preview.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDebounce } from '../hooks/useDebounce';
import { PromptTemplate, promptTemplateService } from '../services/promptTemplateService';
import './SlashCommandAutocomplete.css';

interface SlashCommandAutocompleteProps {
  isVisible: boolean;
  searchQuery: string;
  onSelect: (template: PromptTemplate) => void;
  onClose: () => void;
  position: { top: number; left: number };
}

const SlashCommandAutocomplete: React.FC<SlashCommandAutocompleteProps> = ({
  isVisible,
  searchQuery,
  onSelect,
  onClose,
  position,
}) => {
  const [templates, setTemplates] = useState<PromptTemplate[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectedItemRef = useRef<HTMLDivElement>(null);

  // Debounce search query to avoid excessive API calls
  const debouncedSearchQuery = useDebounce(searchQuery, 200);

  // Fetch templates based on search query
  const fetchTemplates = useCallback(async (query: string) => {
    if (!query.trim()) {
      setTemplates([]);
      return;
    }

    console.log('🔍 Fetching templates for query:', query);
    setLoading(true);
    setError(null);

    try {
      const data = await promptTemplateService.searchTemplates(query, 10);
      console.log('📋 Templates search result:', data);
      console.log('=== FRONTEND TEMPLATES DEBUG ===');
      data.suggestions?.forEach((template, index) => {
        console.log(`Template ${index + 1}:`, {
          id: template.id,
          title: template.title,
          hasPrompt: !!template.prompt,
          promptLength: template.prompt?.length || 0,
          isGlobal: template.isGlobal,
          promptPreview: template.prompt?.substring(0, 100) + '...'
        });
      });
      console.log('=================================');
      setTemplates(data.suggestions || []);
      setSelectedIndex(0); // Reset selection to first item
    } catch (error) {
      console.error('❌ Error fetching templates:', error);
      setError('Failed to load templates');
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Trigger search when debounced query changes
  useEffect(() => {
    if (isVisible && debouncedSearchQuery) {
      fetchTemplates(debouncedSearchQuery);
    } else {
      setTemplates([]);
    }
  }, [isVisible, debouncedSearchQuery, fetchTemplates]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isVisible || templates.length === 0) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setSelectedIndex((prev) => Math.min(prev + 1, templates.length - 1));
          break;
        case 'ArrowUp':
          event.preventDefault();
          setSelectedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          event.preventDefault();
          if (templates[selectedIndex]) {
            onSelect(templates[selectedIndex]);
          }
          break;
        case 'Escape':
          event.preventDefault();
          onClose();
          break;
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isVisible, templates, selectedIndex, onSelect, onClose]);

  // Scroll selected item into view
  useEffect(() => {
    if (selectedItemRef.current) {
      selectedItemRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [selectedIndex]);

  // Handle clicks outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isVisible, onClose]);

  // Reset selected index when templates change
  useEffect(() => {
    setSelectedIndex(0);
  }, [templates]);

  if (!isVisible) return null;

  console.log('SlashCommandAutocomplete rendering:', { 
    isVisible, 
    position, 
    templatesLength: templates.length,
    loading,
    error,
    searchQuery: debouncedSearchQuery 
  });

  return (
    <div
      ref={dropdownRef}
      className="slash-command-autocomplete"
      style={{
        position: 'absolute',
        bottom: 'calc(100% + 8px)', // Position above the input with 8px gap
        left: 0,
        right: 0,
        zIndex: 50,
      }}
    >
      <div className="autocomplete-dropdown">
        {loading && (
          <div className="autocomplete-loading">
            <div className="loading-spinner"></div>
            <span>Searching templates...</span>
          </div>
        )}

        {error && (
          <div className="autocomplete-error">
            <span>{error}</span>
          </div>
        )}

        {!loading && !error && templates.length === 0 && debouncedSearchQuery && (
          <div className="autocomplete-empty">
            <span>No templates found for "{debouncedSearchQuery}"</span>
          </div>
        )}

        {!loading && !error && templates.length === 0 && !debouncedSearchQuery && (
          <div className="autocomplete-hint">
            <span>Type to search templates...</span>
          </div>
        )}

        {!loading && !error && templates.length > 0 && (
          <>
            <div className="autocomplete-header">
              <span className="result-count">{templates.length} template{templates.length !== 1 ? 's' : ''}</span>
              <span className="navigation-hint">↑↓ navigate • Enter select • Esc close</span>
            </div>
            <div className="autocomplete-list">
              {templates.map((template, index) => (
                <div
                  key={template.id}
                  ref={index === selectedIndex ? selectedItemRef : null}
                  className={`autocomplete-item ${index === selectedIndex ? 'selected' : ''}`}
                  onClick={() => onSelect(template)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <div className="template-header">
                    <span className="template-title">{template.title}</span>
                    <div className="template-badges">
                      {(() => {
                        const scope = template.scope ?? (template.isGlobal ? 'global' : template.isCompanyTemplate ? 'company' : 'personal');
                        if (scope === 'global') return <span className="badge global">Global</span>;
                        if (scope === 'company') return <span className="badge company">Company</span>;
                        return <span className="badge personal">Personal</span>;
                      })()}
                      <span className="badge category">{template.category}</span>
                      {template.usageCount > 0 && (
                        <span className="badge usage">{template.usageCount} uses</span>
                      )}
                    </div>
                  </div>
                  {template.description && (
                    <div className="template-description">
                      {template.description}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        <div className="autocomplete-footer">
          <span>Type "/" to insert template • ⌘K for template management</span>
        </div>
      </div>
    </div>
  );
};

export default SlashCommandAutocomplete;